import React, { useEffect, useState } from 'react';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import useIdList from '../../../../../hooks/useIdList';
import { getIntl } from '../../../../../utils/intl';
import useResource from '../../../../../hooks/api/useResource';
import CareTeamMemberListCard from './CareTeamMemberListCard';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useFilterCareTeamMembers } from './useFilterCareTeamMembers';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import SearchInput from '../../../../v2BaseComponents/SearchInput';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { useListEditActions, ListMode } from '../../../../../hooks/useListEditActions';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import { mainContentPadding } from '../../../../v2BaseComponents/MainContentContainer';
import { LinkEmptyStateHint } from '../../../../shared/EmptyStateHint';

const intl = getIntl('relatedCareTeamMemberList', {
    careTeamMember: 'Care Team Member',
    careTeamMembers: 'Care Team Members',
    updateMessage: 'Adding {add} And Removing {remove}',
    placeholder: 'Search',
});

export type CareTeamMemberListOwnProps = {
    relatedCareTeamMembers: APITypes.medicalProfileService.CareTeamMemberDetails[];
    allCareTeamMembers: APITypes.medicalProfileService.CareTeamMemberDetails[];
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    profileId: string;
    resourceId: string;
    isLoadingCareTeamMembers: boolean;
    hasEditPermission: boolean;
    resourceGroupIds: string[];
};

export type CareTeamMemberListProps = CareTeamMemberListOwnProps;

const CareTeamMemberList: React.FC<CareTeamMemberListProps> = ({
    relatedCareTeamMembers,
    allCareTeamMembers,
    profileId,
    resourceId,
    isLoadingCareTeamMembers,
    hasEditPermission,
    category,
    resourceGroupIds,
}) => {
    const navigator = useProfilesTabNavigation();
    const defaultRelatedCareTeamMemberIds = [...relatedCareTeamMembers].map(
        (careTeamMember) => careTeamMember.careTeamMemberId,
    );
    const [listMode, setListMode] = useState<ListMode>('related');
    const { onUpdateResourceCareTeam, isUpdatingResourceCareTeam } = useResource({
        medicalProfileId: profileId,
        resourceId,
        resourceCategory: category,
        onUpdateResourceCareTeamSuccess: () => {
            setListMode('related');
            resetIdList();
        },
    });
    const { idList, addId, removeId, updateMessage, resetIdList } = useIdList({
        initialIdList: defaultRelatedCareTeamMemberIds,
        getUpdateMessage: ({ addCount, removeCount, isAddPlural, isRemovePlural }) => {
            const getResourceMessage = (isPlural: boolean, count: number) => {
                const resourceMessage = !isPlural ? intl('careTeamMember') : intl('careTeamMembers');
                return `${count} ${resourceMessage}`;
            };

            return intl('updateMessage', {
                add: getResourceMessage(isAddPlural, addCount),
                remove: getResourceMessage(isRemovePlural, removeCount),
            });
        },
    });
    const [searchInput, setSearchInput] = useState<string>('');
    useEffect(() => {
        setSearchInput('');
    }, [listMode]);
    const filteredCareTeamMembers = useFilterCareTeamMembers(allCareTeamMembers, searchInput);
    const listEditActions = useListEditActions({
        listMode,
        canEdit: hasEditPermission,
        onEdit: () => setListMode('link'),
        onCancel: () => {
            setListMode('related');
            resetIdList();
        },
        onAdd: () => {
            navigator.navigate('add-care-team', {
                profileId,
                associatedResourceId: resourceId,
            });
        },
        onSubmitLinkUpdates: () => {
            onUpdateResourceCareTeam({
                careTeamMemberIds: idList,
                resourceGroupIds,
            });
        },
        isDisabled: isUpdatingResourceCareTeam,
        promptChangeDescription: updateMessage,
    });

    const renderList = () => {
        if (listMode === 'related') {
            return (
                <ActionButtonLayout scrollable actions={listEditActions.actions}>
                    {relatedCareTeamMembers?.length ? (
                        <Stack vertical fullWidth space={contentStackSpacing} style={{ padding: mainContentPadding }}>
                            {(relatedCareTeamMembers || []).map((careTeamMember) => (
                                <CareTeamMemberListCard
                                    key={careTeamMember.careTeamMemberId}
                                    careTeamMember={careTeamMember}
                                    profileId={profileId}
                                    isSelectable={false}
                                    onNavigateToDetails={() => {
                                        navigator.navigate('care-team-member', {
                                            profileId,
                                            careTeamMemberId: careTeamMember.careTeamMemberId,
                                        });
                                    }}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <LinkEmptyStateHint variant="care-team" />
                    )}
                </ActionButtonLayout>
            );
        } else {
            return (
                <Stack vertical fullWidth style={{ flex: 1 }}>
                    <HeaderContainer>
                        <SearchInput
                            placeholder={intl('placeholder')}
                            value={searchInput}
                            onChange={setSearchInput}
                            isDisabled={!allCareTeamMembers?.length}
                        />
                    </HeaderContainer>
                    <ActionButtonLayout scrollable actions={listEditActions.actions}>
                        <Stack vertical fullWidth style={{ padding: mainContentPadding }} space={contentStackSpacing}>
                            {(filteredCareTeamMembers || []).map((careTeamMember) => {
                                const isSelected = idList.includes(careTeamMember.careTeamMemberId);
                                return (
                                    <CareTeamMemberListCard
                                        key={careTeamMember.careTeamMemberId}
                                        careTeamMember={careTeamMember}
                                        profileId={profileId}
                                        isSelectable={true}
                                        isSelected={isSelected}
                                        onSelect={() => {
                                            if (isSelected) {
                                                removeId(careTeamMember.careTeamMemberId);
                                            } else {
                                                addId(careTeamMember.careTeamMemberId);
                                            }
                                        }}
                                        isDisabled={isUpdatingResourceCareTeam}
                                    />
                                );
                            })}
                        </Stack>
                    </ActionButtonLayout>
                </Stack>
            );
        }
    };

    return (
        <>
            <AnimateLoadedContent isLoading={isLoadingCareTeamMembers}>{() => renderList()}</AnimateLoadedContent>
            {listEditActions.Prompt}
        </>
    );
};

export default React.memo(CareTeamMemberList);
